import { device } from "@helpers";
import { fetchWithTimeout } from "@utils/fetchWithTimeout";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

const defaultIconSizes = {
	iconMobile: 16,
	iconTablet: 21,
	iconLaptop: 21,
	iconDesktop: 31,
	iconDesktopLarge: 31,
};

const IconImage = styled.img<{
	iconSizes: { [key: string]: number };
	iconStyles: { [key: string]: string };
	styles: any;
	color: string;
}>`
  width: ${(props) => props.iconSizes.iconMobile || defaultIconSizes.iconMobile}px !important;
  height: ${(props) =>
		props.iconSizes.iconMobile || defaultIconSizes.iconMobile}px !important;

  ${(props) => props.iconStyles}

  :hover {
    cursor: ${({ onClick }) => (onClick ? "pointer" : "auto")};
  }

  &:before {
    ${(props) => props.styles}

    font-size: ${(props) =>
			props.iconSizes.iconMobile || defaultIconSizes.iconMobile}px !important;

    ${(props) => (props.color ? `color: ${props.color}` : "")}
  }

  @media (min-width: ${device.tablet}) and (max-width: ${device.tabletL}) {
    width: ${(props) =>
			props.iconSizes.iconTablet || defaultIconSizes.iconTablet}px !important;
    height: ${(props) =>
			props.iconSizes.iconTablet || defaultIconSizes.iconTablet}px !important;

    &:before {
      font-size: ${(props) =>
				props.iconSizes.iconTablet || defaultIconSizes.iconTablet}px !important;
    }
  }

  @media (min-width: ${device.laptop}) and (max-width: ${device.desktopS}) {
    width: ${(props) =>
			props.iconSizes.iconLaptop || defaultIconSizes.iconLaptop}px !important;
    height: ${(props) =>
			props.iconSizes.iconLaptop || defaultIconSizes.iconLaptop}px !important;

    &:before {
      font-size: ${(props) =>
				props.iconSizes.iconLaptop || defaultIconSizes.iconLaptop}px !important;
    }
  }

  @media (min-width: ${device.desktopS}) and (max-width: ${device.desktop}) {
    width: ${(props) =>
			props.iconSizes.iconDesktop || defaultIconSizes.iconDesktop}px !important;
    height: ${(props) =>
			props.iconSizes.iconDesktop || defaultIconSizes.iconDesktop}px !important;

    &:before {
      font-size: ${(props) =>
				props.iconSizes.iconDesktop ||
				defaultIconSizes.iconDesktop}px !important;
    }
  }

  @media (min-width: ${device.desktop}) {
    width: ${(props) =>
			props.iconSizes.iconDesktopLarge ||
			defaultIconSizes.iconDesktopLarge}px !important;
    height: ${(props) =>
			props.iconSizes.iconDesktopLarge ||
			defaultIconSizes.iconDesktopLarge}px !important;

    &:before {
      font-size: ${(props) =>
				props.iconSizes.iconDesktopLarge ||
				defaultIconSizes.iconDesktopLarge}px !important;
    }
  }
`;

const Icon = ({ iconSizes = {}, styles = {}, iconStyles = {}, ...props }) => {
	const { iconName, color = "red", alt = "", ...rest } = props;
	const [iconSrc, setIconSrc] = useState<string | null>(null);
	const name = iconName?.toLowerCase();

	useEffect(() => {
		const resolve = async () => {
			const svgPath = `/icons/${name}.svg`;
			try {
				const res = await fetchWithTimeout(svgPath, {
					method: "HEAD",
					throwOnHttpError: false,
				});
				setIconSrc(res.ok ? svgPath : `/icons/${name}.png`);
			} catch {
				setIconSrc(`/icons/${name}.png`);
			}
		};
		void resolve();
	}, [name]);

	if (!iconSrc) {
		return null;
	}

	if (iconSrc.endsWith(".png")) {
		return <img {...rest} alt={alt} src={iconSrc} />;
	}

	return (
		<IconImage
			{...rest}
			alt={alt}
			color={color}
			className={iconSrc}
			src={iconSrc}
			iconSizes={iconSizes}
			iconStyles={iconStyles}
			styles={styles}
			{...props}
		/>
	);
};

export default Icon;
